import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { EditConnectionActionDialogBody } from '~src/connectivity/components/EditConnection/components/EditConnectionActionDialogBody';
import { EditConnectionReactActionUtils } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionUtils';
import { EditableConnection } from '~src/connectivity/services/EditConnectionDialog';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { RemoteConnectionDetailInfo } from '~src/ts/models/ConnectionDetailInfo';
import { IDataConnectionInfo } from '~src/ts/models/IDataConnectionInfo';
import { ICheckConnectionResult } from '~src/ts/ServerApi/requests/CheckConnectionRequest';

import {
    ReadOnlyEditableConnection} from './utils/EditConnectionActionTypes';

export interface EditConnectionDialogProps {
    prompt: string;
    isServerNameDisabled: boolean;
    isServerPortDisabled: boolean;
    isUsernameVisible: boolean;
    isEmbeddedToggleVisible: boolean;
    isTestConnectionButtonVisible: boolean;
    showNetworkType: boolean;
    isPortValid: (port: string) => boolean;
    onTestConnection: (dialogState: EditConnectionDialogState) => Promise<ICheckConnectionResult>;
}

export interface EditConnectionDialogState {
    serverName: string | undefined;
    serverPort: string | undefined;
    username: string | undefined;
    isPasswordEmbedded: boolean;
    newPassword: string | undefined;
    usingRemoteQueryAgent: boolean | undefined;
}

export interface StateAndProps {
    editConnectionDialogProps: EditConnectionDialogProps;
    editConnectionDialogState: EditConnectionDialogState;
}

export let PasswordUnchanged: string = null;
const GENERIC_JDBC_CONNECTION_TYPE = 'genericjdbc';
export namespace EditConnectionAction {

    export async function confirmEditDatabaseConnections(
        connections: EditableConnection[]): Promise<Promise<any>> {
        const { editConnectionDialogProps, editConnectionDialogState } = await createDialogStateAndProps(connections);

        const modalInstance = openReactActionDialog<EditConnectionDialogProps, EditConnectionDialogState, IResult>({
            title: Messages.editConnection(),
            bodyComponent: EditConnectionActionDialogBody,
            initialBodyProps: editConnectionDialogProps,
            initialDialogState: editConnectionDialogState,
            actionName: Messages.save(),
            action: (_props, dialogState) => EditConnectionReactActionUtils.applyConnectionEdits(connections, dialogState),
            dialogWidth: TabDialogWidth.width375px,
            dismissDialogOnActionFailed: true,
            actionDisabled: (_bodyProps: EditConnectionDialogProps, dialogState: EditConnectionDialogState) => !EditConnectionReactActionUtils.isPortValid(dialogState.serverPort)
        });

        return modalInstance.result;
    }

    async function createDialogStateAndProps(
        connections: EditableConnection[]
    ) {
        const firstConnection: ReadOnlyEditableConnection | undefined = connections[0];
        const datasource: IDataSourceInfo = ((firstConnection as IDataConnectionInfo).datasource) as IDataSourceInfo;
        const showTestConnection: boolean = connections.length === 1 && firstConnection.testingConnectionAllowed;

        const testConnection = (dialogState: EditConnectionDialogState): Promise<ICheckConnectionResult> => {
            return EditConnectionReactActionUtils.checkEditedConnection(firstConnection.id, dialogState);
        };

        const canSessionUserEditEmbeddedCredentials: boolean = getSessionManagementServiceInstance().canSessionUserEditEmbeddedCredentials();
        const canEmbedPassword: boolean = await EditConnectionReactActionUtils.canEmbedPassword(datasource);

        const editConnectionDialogProps: EditConnectionDialogProps = {
            prompt: EditConnectionReactActionUtils.makePrompt(connections.length),
            isServerNameDisabled: firstConnection ? firstConnection.connectionType === GENERIC_JDBC_CONNECTION_TYPE : false,
            isServerPortDisabled: firstConnection ? firstConnection.connectionType === GENERIC_JDBC_CONNECTION_TYPE : false,
            isUsernameVisible: canSessionUserEditEmbeddedCredentials,
            isEmbeddedToggleVisible: canSessionUserEditEmbeddedCredentials && canEmbedPassword,
            isTestConnectionButtonVisible: showTestConnection,
            showNetworkType: getSessionManagementServiceInstance().isOnlineEnvironment() &&
                EditConnectionReactActionUtils.showNetworkType(datasource),
            isPortValid: EditConnectionReactActionUtils.isPortValid,
            onTestConnection: testConnection
        };

        const editConnectionDialogState: EditConnectionDialogState = {
            serverName: EditConnectionReactActionUtils.uniqueValueOrMixed(connections, connection => (connection.connectionDetails as RemoteConnectionDetailInfo).serverName),
            serverPort: EditConnectionReactActionUtils.uniqueValueOrMixed(connections, connection => (connection.connectionDetails as RemoteConnectionDetailInfo).serverPort),
            username: EditConnectionReactActionUtils.uniqueValueOrMixed(connections, connection => (connection.connectionDetails as RemoteConnectionDetailInfo).databaseUsername),
            isPasswordEmbedded: EditConnectionReactActionUtils.uniqueValueOrUndefined(connections, connection => (connection.connectionDetails as RemoteConnectionDetailInfo).hasEmbeddedPassword),
            newPassword: PasswordUnchanged,
            usingRemoteQueryAgent: connections.length >= 1 && firstConnection.usingRemoteQueryAgent
        };

        return { editConnectionDialogProps, editConnectionDialogState };
    }

}
